import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  GraduationCap,
  Shield,
  Plus, 
  Eye,
  Trash2,
  Users,
  Calendar,
  Clock,
  MapPin,
  Building,
  FileText,
  Upload
} from 'lucide-react';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';

export default function SessionsFormationCACES() {
  const { currentUser, typesFormations, categoriesCACES, collaborateurs } = useApp();
  const [activeTab, setActiveTab] = useState('formations');
  const [sessionsFormations, setSessionsFormations] = useState([]);
  const [sessionsCACES, setSessionsCACES] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [createFormationDialogOpen, setCreateFormationDialogOpen] = useState(false);
  const [createCACESDialogOpen, setCreateCACESDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionType, setSessionType] = useState('formation');
  
  // Form states
  const [formationForm, setFormationForm] = useState({
    type_formation_id: '',
    date_formation: '',
    heure_debut: '09:00',
    heure_fin: '17:00',
    duree_heures: 7,
    lieu: '',
    formateur: '',
    organisme: '',
    numero_attestation: '',
    justificatif_url: '',
    collaborateurs_ids: []
  });
  
  const [cacesForm, setCacesForm] = useState({
    categorie_id: '',
    date_formation: '',
    date_fin: '',
    heure_debut: '09:00',
    heure_fin: '17:00',
    duree_heures: 7,
    lieu: '',
    formateur: '',
    organisme: '',
    numero_certificat: '',
    justificatif_url: '',
    collaborateurs_ids: []
  });

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const [formations, caces] = await Promise.all([
        api.getSessionsFormations(),
        api.getSessionsCACES()
      ]);
      setSessionsFormations(formations);
      setSessionsCACES(caces);
    } catch (error) {
      toast.error('Erreur lors du chargement des sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleCollaborateurToggle = (collabId, formType) => {
    if (formType === 'formation') {
      const current = formationForm.collaborateurs_ids;
      if (current.includes(collabId)) {
        setFormationForm({ ...formationForm, collaborateurs_ids: current.filter(id => id !== collabId) });
      } else {
        setFormationForm({ ...formationForm, collaborateurs_ids: [...current, collabId] });
      }
    } else {
      const current = cacesForm.collaborateurs_ids;
      if (current.includes(collabId)) {
        setCacesForm({ ...cacesForm, collaborateurs_ids: current.filter(id => id !== collabId) });
      } else {
        setCacesForm({ ...cacesForm, collaborateurs_ids: [...current, collabId] });
      }
    }
  };

  const handleCreateFormationSession = async (e) => {
    e.preventDefault();
    if (formationForm.collaborateurs_ids.length === 0) {
      toast.error('Sélectionnez au moins un collaborateur');
      return;
    }
    
    try {
      await api.createSessionFormation(
        formationForm,
        currentUser.id,
        `${currentUser.prenom} ${currentUser.nom}`
      );
      toast.success(`Session créée pour ${formationForm.collaborateurs_ids.length} collaborateurs`);
      setCreateFormationDialogOpen(false);
      resetFormationForm();
      loadSessions();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la création');
    }
  };

  const handleCreateCACESSession = async (e) => {
    e.preventDefault();
    if (cacesForm.collaborateurs_ids.length === 0) {
      toast.error('Sélectionnez au moins un collaborateur');
      return;
    }
    
    try {
      await api.createSessionCACES(
        cacesForm,
        currentUser.id,
        `${currentUser.prenom} ${currentUser.nom}`
      );
      toast.success(`Session CACES créée pour ${cacesForm.collaborateurs_ids.length} collaborateurs`);
      setCreateCACESDialogOpen(false);
      resetCACESForm();
      loadSessions();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la création');
    }
  };

  const handleDeleteSession = async () => {
    if (!selectedSession) return;
    try {
      if (sessionType === 'formation') {
        await api.deleteSessionFormation(selectedSession.id);
      } else {
        await api.deleteSessionCACES(selectedSession.id);
      }
      toast.success('Session supprimée');
      setDeleteDialogOpen(false);
      setDetailDialogOpen(false);
      setSelectedSession(null);
      loadSessions();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openSessionDetail = (session, type) => {
    setSelectedSession(session);
    setSessionType(type);
    setDetailDialogOpen(true);
  };

  const resetFormationForm = () => {
    setFormationForm({
      type_formation_id: '',
      date_formation: '',
      heure_debut: '09:00',
      heure_fin: '17:00',
      duree_heures: 7,
      lieu: '',
      formateur: '',
      organisme: '',
      numero_attestation: '',
      justificatif_url: '',
      collaborateurs_ids: []
    });
  };

  const resetCACESForm = () => {
    setCacesForm({
      categorie_id: '',
      date_formation: '',
      date_fin: '',
      heure_debut: '09:00',
      heure_fin: '17:00',
      duree_heures: 7,
      lieu: '',
      formateur: '',
      organisme: '',
      numero_certificat: '',
      justificatif_url: '',
      collaborateurs_ids: []
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sessions de Formation & CACES</h1>
          <p className="text-gray-500">Gérez les sessions avec plusieurs participants</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="formations" className="gap-2">
              <GraduationCap className="h-4 w-4" />
              Sessions Formations
            </TabsTrigger>
            <TabsTrigger value="caces" className="gap-2">
              <Shield className="h-4 w-4" />
              Sessions CACES
            </TabsTrigger>
          </TabsList>
          
          {activeTab === 'formations' ? (
            <Button onClick={() => setCreateFormationDialogOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Nouvelle session formation
            </Button>
          ) : (
            <Button onClick={() => setCreateCACESDialogOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Nouvelle session CACES
            </Button>
          )}
        </div>

        {/* Formations Tab */}
        <TabsContent value="formations">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : sessionsFormations.length === 0 ? (
                <div className="text-center py-12">
                  <GraduationCap className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">Aucune session de formation</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Formation</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Lieu</TableHead>
                      <TableHead>Participants</TableHead>
                      <TableHead>Créé par</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessionsFormations.map(session => (
                      <TableRow key={session.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{session.type_formation_code}</Badge>
                            <span className="font-medium">{session.intitule}</span>
                          </div>
                        </TableCell>
                        <TableCell>{session.date_formation}</TableCell>
                        <TableCell>{session.lieu || '-'}</TableCell>
                        <TableCell>
                          <Badge className="bg-blue-100 text-blue-800">
                            <Users className="h-3 w-3 mr-1" />
                            {session.collaborateurs_ids?.length || 0}
                          </Badge>
                        </TableCell>
                        <TableCell>{session.cree_par_nom}</TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openSessionDetail(session, 'formation')}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => {
                                setSelectedSession(session);
                                setSessionType('formation');
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CACES Tab */}
        <TabsContent value="caces">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : sessionsCACES.length === 0 ? (
                <div className="text-center py-12">
                  <Shield className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">Aucune session CACES</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Catégorie CACES</TableHead>
                      <TableHead>Date formation</TableHead>
                      <TableHead>Validité</TableHead>
                      <TableHead>Organisme</TableHead>
                      <TableHead>Participants</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessionsCACES.map(session => (
                      <TableRow key={session.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{session.categorie_code}</Badge>
                            <span className="font-medium">{session.categorie_libelle}</span>
                          </div>
                        </TableCell>
                        <TableCell>{session.date_formation}</TableCell>
                        <TableCell>{session.date_fin}</TableCell>
                        <TableCell>{session.organisme}</TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-800">
                            <Users className="h-3 w-3 mr-1" />
                            {session.collaborateurs_ids?.length || 0}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openSessionDetail(session, 'caces')}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => {
                                setSelectedSession(session);
                                setSessionType('caces');
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Formation Session Dialog */}
      <Dialog open={createFormationDialogOpen} onOpenChange={setCreateFormationDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouvelle session de formation</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateFormationSession}>
            <div className="space-y-4 py-4">
              {/* Type de formation */}
              <div className="space-y-2">
                <Label>Type de formation *</Label>
                <Select 
                  value={formationForm.type_formation_id} 
                  onValueChange={(v) => setFormationForm({ ...formationForm, type_formation_id: v })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {typesFormations.map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{type.code}</Badge>
                          {type.intitule}
                          <span className="text-gray-400 text-xs">
                            ({type.validite_type === 'vie' ? 'À vie' : `${type.validite_mois} mois`})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date & Heures */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={formationForm.date_formation}
                    onChange={(e) => setFormationForm({ ...formationForm, date_formation: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Heure début</Label>
                  <Input
                    type="time"
                    value={formationForm.heure_debut}
                    onChange={(e) => setFormationForm({ ...formationForm, heure_debut: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Heure fin</Label>
                  <Input
                    type="time"
                    value={formationForm.heure_fin}
                    onChange={(e) => setFormationForm({ ...formationForm, heure_fin: e.target.value })}
                  />
                </div>
              </div>

              {/* Lieu & Formateur */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Lieu</Label>
                  <Input
                    value={formationForm.lieu}
                    onChange={(e) => setFormationForm({ ...formationForm, lieu: e.target.value })}
                    placeholder="Salle de formation..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Formateur</Label>
                  <Input
                    value={formationForm.formateur}
                    onChange={(e) => setFormationForm({ ...formationForm, formateur: e.target.value })}
                    placeholder="Nom du formateur"
                  />
                </div>
              </div>

              {/* Organisme & Attestation */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Organisme</Label>
                  <Input
                    value={formationForm.organisme}
                    onChange={(e) => setFormationForm({ ...formationForm, organisme: e.target.value })}
                    placeholder="AFPA, AFTRAL..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>N° Attestation</Label>
                  <Input
                    value={formationForm.numero_attestation}
                    onChange={(e) => setFormationForm({ ...formationForm, numero_attestation: e.target.value })}
                    placeholder="ATT-2024-001"
                  />
                </div>
              </div>

              {/* Justificatif URL */}
              <div className="space-y-2">
                <Label>URL du justificatif (PDF)</Label>
                <Input
                  value={formationForm.justificatif_url}
                  onChange={(e) => setFormationForm({ ...formationForm, justificatif_url: e.target.value })}
                  placeholder="https://..."
                />
                <p className="text-xs text-gray-500">Document commun pour tous les participants</p>
              </div>

              {/* Sélection des collaborateurs */}
              <div className="space-y-2">
                <Label>Collaborateurs participants * ({formationForm.collaborateurs_ids.length} sélectionnés)</Label>
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {collaborateurs.map(collab => (
                    <div 
                      key={collab.id}
                      className="flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleCollaborateurToggle(collab.id, 'formation')}
                    >
                      <Checkbox 
                        checked={formationForm.collaborateurs_ids.includes(collab.id)}
                        onCheckedChange={() => handleCollaborateurToggle(collab.id, 'formation')}
                      />
                      <div className="flex-1">
                        <p className="font-medium">{collab.prenom} {collab.nom}</p>
                        <p className="text-xs text-gray-500">{collab.matricule}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateFormationDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={formationForm.collaborateurs_ids.length === 0}>
                Créer la session
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create CACES Session Dialog */}
      <Dialog open={createCACESDialogOpen} onOpenChange={setCreateCACESDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouvelle session CACES</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCACESSession}>
            <div className="space-y-4 py-4">
              {/* Catégorie CACES */}
              <div className="space-y-2">
                <Label>Catégorie CACES *</Label>
                <Select 
                  value={cacesForm.categorie_id} 
                  onValueChange={(v) => setCacesForm({ ...cacesForm, categorie_id: v })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriesCACES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{cat.code}</Badge>
                          {cat.libelle}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de formation *</Label>
                  <Input
                    type="date"
                    value={cacesForm.date_formation}
                    onChange={(e) => setCacesForm({ ...cacesForm, date_formation: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de fin de validité *</Label>
                  <Input
                    type="date"
                    value={cacesForm.date_fin}
                    onChange={(e) => setCacesForm({ ...cacesForm, date_fin: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Heures */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Heure début</Label>
                  <Input
                    type="time"
                    value={cacesForm.heure_debut}
                    onChange={(e) => setCacesForm({ ...cacesForm, heure_debut: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Heure fin</Label>
                  <Input
                    type="time"
                    value={cacesForm.heure_fin}
                    onChange={(e) => setCacesForm({ ...cacesForm, heure_fin: e.target.value })}
                  />
                </div>
              </div>

              {/* Lieu & Formateur */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Lieu</Label>
                  <Input
                    value={cacesForm.lieu}
                    onChange={(e) => setCacesForm({ ...cacesForm, lieu: e.target.value })}
                    placeholder="Centre de formation..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Formateur</Label>
                  <Input
                    value={cacesForm.formateur}
                    onChange={(e) => setCacesForm({ ...cacesForm, formateur: e.target.value })}
                    placeholder="Nom du formateur"
                  />
                </div>
              </div>

              {/* Organisme & Certificat */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Organisme *</Label>
                  <Input
                    value={cacesForm.organisme}
                    onChange={(e) => setCacesForm({ ...cacesForm, organisme: e.target.value })}
                    placeholder="AFPA, AFTRAL..."
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>N° Certificat</Label>
                  <Input
                    value={cacesForm.numero_certificat}
                    onChange={(e) => setCacesForm({ ...cacesForm, numero_certificat: e.target.value })}
                    placeholder="CERT-2024-001"
                  />
                </div>
              </div>

              {/* Justificatif URL */}
              <div className="space-y-2">
                <Label>URL du justificatif (PDF)</Label>
                <Input
                  value={cacesForm.justificatif_url}
                  onChange={(e) => setCacesForm({ ...cacesForm, justificatif_url: e.target.value })}
                  placeholder="https://..."
                />
                <p className="text-xs text-gray-500">Certificat commun pour tous les participants</p>
              </div>

              {/* Sélection des collaborateurs */}
              <div className="space-y-2">
                <Label>Collaborateurs participants * ({cacesForm.collaborateurs_ids.length} sélectionnés)</Label>
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {collaborateurs.map(collab => (
                    <div 
                      key={collab.id}
                      className="flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleCollaborateurToggle(collab.id, 'caces')}
                    >
                      <Checkbox 
                        checked={cacesForm.collaborateurs_ids.includes(collab.id)}
                        onCheckedChange={() => handleCollaborateurToggle(collab.id, 'caces')}
                      />
                      <div className="flex-1">
                        <p className="font-medium">{collab.prenom} {collab.nom}</p>
                        <p className="text-xs text-gray-500">{collab.matricule}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateCACESDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={cacesForm.collaborateurs_ids.length === 0}>
                Créer la session
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Session Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          {selectedSession && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {sessionType === 'formation' ? (
                    <GraduationCap className="h-5 w-5" />
                  ) : (
                    <Shield className="h-5 w-5" />
                  )}
                  {sessionType === 'formation' 
                    ? selectedSession.intitule 
                    : `CACES ${selectedSession.categorie_code}`
                  }
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span>Date: {selectedSession.date_formation}</span>
                  </div>
                  {selectedSession.heure_debut && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span>{selectedSession.heure_debut} - {selectedSession.heure_fin}</span>
                    </div>
                  )}
                  {selectedSession.lieu && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span>{selectedSession.lieu}</span>
                    </div>
                  )}
                  {selectedSession.organisme && (
                    <div className="flex items-center gap-2">
                      <Building className="h-4 w-4 text-gray-400" />
                      <span>{selectedSession.organisme}</span>
                    </div>
                  )}
                </div>

                {sessionType === 'caces' && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Validité:</strong> jusqu&apos;au {selectedSession.date_fin}
                    </p>
                  </div>
                )}

                {/* Justificatif */}
                {selectedSession.justificatif_url && (
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <FileText className="h-5 w-5 text-gray-600" />
                    <span className="flex-1">Justificatif commun</span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => window.open(selectedSession.justificatif_url, '_blank')}
                    >
                      Voir le PDF
                    </Button>
                  </div>
                )}

                {/* Participants */}
                <div>
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Participants ({selectedSession.collaborateurs_noms?.length || 0})
                  </h3>
                  <div className="space-y-1">
                    {selectedSession.collaborateurs_noms?.map((nom, idx) => (
                      <div key={idx} className="p-2 bg-gray-50 rounded text-sm">
                        {nom}
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-xs text-gray-500">
                  Créée par {selectedSession.cree_par_nom} le {new Date(selectedSession.date_creation).toLocaleDateString('fr-FR')}
                </p>
              </div>
              
              <DialogFooter>
                <Button 
                  variant="destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                  className="mr-auto"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </Button>
                <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                  Fermer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action supprimera la session et toutes les {sessionType === 'formation' ? 'formations' : 'certifications CACES'} associées aux participants.
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSession} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
